export interface ServiceQuestionOption {
  label: string;
  value: string;
  subtext?: string;
}

export interface ServiceQuestion {
  id: number;
  question: string;
  description: string;
  options: ServiceQuestionOption[];
}

export interface PricingPlan {
  tier: 'basic' | 'standard' | 'premium';
  name: string;
  price?: number;
  features: string[];
  popular?: boolean;
}

export interface ServiceDetailData {
  id: string;
  title: string;
  category: 'startups' | 'income-tax' | 'tax-planning' | 'gst' | 'mca';
  categoryTitle: string;
  tagline: string;
  startingPrice?: number;
  timeline: string;
  overview: string;
  documentsRequired: string[];
  processSteps: { step: number; title: string; desc: string }[];
  pricingPlans: PricingPlan[];
  questionnaire: ServiceQuestion[];
  faqs: { question: string; answer: string }[];
}

export const masterServicesData: Record<string, ServiceDetailData> = {
  'private-company': {
    id: 'private-company',
    title: 'Private Limited Company Registration',
    category: 'startups',
    categoryTitle: 'Startups & Registration',
    tagline: 'Incorporate your Private Limited Company with 2 DSCs, SPICe+ MCA filing & PAN/TAN in 5-7 days.',
    startingPrice: 7999,
    timeline: '5-7 Business Days',
    overview: 'Private Limited Company is India\'s most trusted legal corporate structure for startups and growing enterprises. It grants limited liability protection, separates personal assets, and is mandatory for equity investment from VCs and Angel networks.',
    documentsRequired: [
      'PAN Card of all Directors & Shareholders',
      'Aadhaar Card / Voter ID / Passport of Directors',
      'Bank Statement or Electricity Bill (latest 2 months)',
      'Registered Office Proof (Electricity/Gas Bill + NOC from Owner)',
      'Passport size photos of Directors'
    ],
    processSteps: [
      { step: 1, title: 'Name Approval (RUN / SPICe+ Part A)', desc: 'Reserve unique corporate brand name with MCA.' },
      { step: 2, title: 'DSC & Document Preparation', desc: 'Obtain Class-3 Digital Signature Certificates & draft MoA/AoA.' },
      { step: 3, title: 'SPICe+ MCA E-Filing', desc: 'File SPICe+ Part B, AGILE-PRO-S for PAN, TAN, GST & Bank Account.' },
      { step: 4, title: 'COI Issuance & Bank Account', desc: 'Receive Certificate of Incorporation with CIN, PAN & TAN.' }
    ],
    pricingPlans: [
      { tier: 'basic', name: 'Basic Package', price: 7999, features: ['Certificate of Incorporation', '2 Class-3 DSCs', 'PAN & TAN Allotment', 'Standard Email Support'] },
      { tier: 'standard', name: 'Standard Package', price: 11999, popular: true, features: ['Includes Basic Package', 'Name Reservation (RUN)', 'Custom MoA & AoA Drafting', 'GST Registration', 'Dedicated CA Support'] },
      { tier: 'premium', name: 'Premium Startup Bundle', price: 15999, features: ['Includes Standard Package', 'Trademark Brand Application', 'DPIIT Startup India Recognition', 'MSME Udyam Registration', '1-Year Retainer CA Consultation'] }
    ],
    questionnaire: [
      {
        id: 1,
        question: "How many directors or co-founders will be in the company?",
        description: "Companies Act 2013 requires a minimum of 2 directors for Private Limited incorporation.",
        options: [
          { label: "2 Co-Founders / Directors", value: "2_directors", subtext: "Minimum requirement satisfied" },
          { label: "3 to 5 Directors", value: "multi_directors", subtext: "Multi-founder equity split" },
          { label: "1 Director Only", value: "single_director", subtext: "Consider One Person Company (OPC) structure" }
        ]
      },
      {
        id: 2,
        question: "Do you plan to raise VC or Angel equity funding in the next 12-24 months?",
        description: "Venture capital funds strictly require Private Limited structure with ESOP allocation capability.",
        options: [
          { label: "Yes, actively preparing for equity funding", value: "yes_vc", subtext: "Pvt Ltd with custom share capital structure" },
          { label: "No, self-funded / bootstrap growth", value: "no_vc", subtext: "Standard capital structure is sufficient" }
        ]
      },
      {
        id: 3,
        question: "Is your business name already registered or trademarked?",
        description: "MCA will reject name reservation if identical or phonetically similar trademarks exist.",
        options: [
          { label: "Brand name checked & unique", value: "name_checked", subtext: "Ready for SPICe+ Part A filing" },
          { label: "Need CA assistance for trademark search", value: "need_search", subtext: "Includes MCA & TM search check" }
        ]
      },
      {
        id: 4,
        question: "Do you have a commercial physical address or home address for office registration?",
        description: "Any residential or commercial utility bill with owner NOC can be used as registered office proof.",
        options: [
          { label: "Commercial Office Rent Agreement", value: "commercial_rent", subtext: "Standard commercial address proof" },
          { label: "Residential Home Address", value: "home_address", subtext: "100% legal for startup registration" }
        ]
      }
    ],
    faqs: [
      { question: "What is the minimum capital required to start a Pvt Ltd?", answer: "There is no minimum paid-up capital requirement under the Companies Act. You can start with as low as ₹1,000." },
      { question: "Can a salaried person become a director in a Pvt Ltd company?", answer: "Yes, a salaried individual can become a director unless restricted by their employment contract." },
      { question: "Is physical presence required at the MCA office?", answer: "No, the entire process is 100% online and paperless via digital signatures." }
    ]
  },

  'llp': {
    id: 'llp',
    title: 'LLP (Limited Liability Partnership) Registration',
    category: 'startups',
    categoryTitle: 'Startups & Registration',
    tagline: 'Combine limited liability protection with lower annual compliance costs for professional services & partnerships.',
    startingPrice: 5999,
    timeline: '5-7 Business Days',
    overview: 'Limited Liability Partnership (LLP) offers the benefit of limited liability to its partners at lower compliance costs. It is ideal for service providers, consultants, software agencies, and small businesses.',
    documentsRequired: [
      'PAN Card of Designated Partners',
      'Aadhaar Card / Voter ID / Passport',
      'Proof of Address (Bank Statement/Utility Bill)',
      'Registered Office Address Proof (Utility Bill + NOC)',
      'Digital Signature Certificate (Class 3)'
    ],
    processSteps: [
      { step: 1, title: 'RUN-LLP Name Approval', desc: 'Reserve unique partnership name with Ministry of Corporate Affairs.' },
      { step: 2, title: 'FiLLiP E-Form Submission', desc: 'Submit FiLLiP form for partner DIN allotment & LLP incorporation.' },
      { step: 3, title: 'Certificate of Incorporation', desc: 'Receive official LLP Identification Number (LLPIN) & PAN/TAN.' },
      { step: 4, title: 'Form 3 LLP Agreement Drafting', desc: 'Draft and execute LLP Agreement within 30 days of registration.' }
    ],
    pricingPlans: [
      { tier: 'basic', name: 'Basic LLP Package', price: 5999, features: ['LLP Incorporation Certificate', '2 Partner Class-3 DSCs', 'PAN & TAN Generation', 'Standard Email Support'] },
      { tier: 'standard', name: 'Standard LLP Bundle', price: 7999, popular: true, features: ['Includes Basic Package', 'Name Reservation (RUN-LLP)', 'Form 3 LLP Agreement Drafting', 'GST Registration', 'Priority CA Support'] },
      { tier: 'premium', name: 'Complete LLP Retainer', price: 9999, features: ['Includes Standard Package', 'Form 11 & Form 8 Annual Filing Support', 'MSME Registration', 'Trademark Brand Filing', 'Dedicated CA Advisory'] }
    ],
    questionnaire: [
      {
        id: 1,
        question: "How many Designated Partners will hold capital in the LLP?",
        description: "At least 2 Designated Partners (at least one Indian resident) are required by law.",
        options: [
          { label: "2 Designated Partners", value: "2_partners", subtext: "Standard LLP setup" },
          { label: "3 or more Partners", value: "multi_partners", subtext: "Multi-partner profit sharing" }
        ]
      },
      {
        id: 2,
        question: "What is the proposed profit-sharing ratio between partners?",
        description: "LLP Agreement (Form 3) requires exact capital contribution & profit split percentage.",
        options: [
          { label: "Equal 50:50 Split", value: "equal_split", subtext: "Standard equal partnership" },
          { label: "Custom Ratio (e.g. 70:30, 60:40)", value: "custom_split", subtext: "Custom drafting in LLP Agreement" }
        ]
      },
      {
        id: 3,
        question: "Do you require GST Registration alongside LLP incorporation?",
        description: "GST is mandatory if turnover exceeds ₹20/40 Lakhs or for interstate services.",
        options: [
          { label: "Yes, include GST Registration", value: "yes_gst", subtext: "Bundled GSTIN application" },
          { label: "No, incorporation only for now", value: "no_gst", subtext: "Apply GST later when turnover grows" }
        ]
      },
      {
        id: 4,
        question: "What is your projected initial capital investment in the LLP?",
        description: "Government stamp duty on LLP agreement depends on total capital contribution.",
        options: [
          { label: "Below ₹1 Lakh", value: "below_1l", subtext: "Standard minimal stamp duty" },
          { label: "₹1 Lakh to ₹10 Lakhs", value: "1l_10l", subtext: "Moderate capital scale" },
          { label: "Above ₹10 Lakhs", value: "above_10l", subtext: "Custom stamp duty schedule" }
        ]
      }
    ],
    faqs: [
      { question: "Is audit mandatory for all LLPs?", answer: "No, audit is mandatory only if annual turnover exceeds ₹40 Lakhs or capital contribution exceeds ₹25 Lakhs." },
      { question: "Can an existing partnership firm convert into an LLP?", answer: "Yes, traditional partnership firms can convert into LLPs under Schedule II of the LLP Act." }
    ]
  },

  'trademark': {
    id: 'trademark',
    title: 'Trademark Brand Name & Logo Protection',
    category: 'startups',
    categoryTitle: 'Startups & Registration',
    tagline: 'Secure legal ownership of your business brand name, logo, and slogan nationwide with official TM symbol.',
    startingPrice: 5999,
    timeline: '1-3 Business Days for TM Application',
    overview: 'Trademark registration protects your brand identity from copycats and unauthorized usage across India. Once filed, you can legally use the ™ symbol immediately and ® upon final certificate issuance.',
    documentsRequired: [
      'Logo / Brand Mark image (JPEG/PNG)',
      'Brand Name text and taglines',
      'Applicant PAN & Aadhaar (or Incorporation Certificate)',
      'MSME/Udyam Certificate (for 50% govt fee concession)',
      'TM-48 Power of Attorney (signed by applicant)'
    ],
    processSteps: [
      { step: 1, title: 'Comprehensive TM Search', desc: 'Check trademark database across 45 classes for prior similar marks.' },
      { step: 2, title: 'Class Identification & Strategy', desc: 'Determine correct Nice Classification classes for your product/service.' },
      { step: 3, title: 'Form TM-A Online Filing', desc: 'File application with Controller General of Patents, Designs & Trademarks.' },
      { step: 4, title: '™ Authorization & Monitoring', desc: 'Receive official TM application number and start using ™ mark immediately.' }
    ],
    pricingPlans: [
      { tier: 'basic', name: 'Basic TM Filing', price: 5999, features: ['Public Search Check', 'Form TM-A Filing (1 Class)', 'Official ™ Symbol Right', 'Filing Receipt Delivery'] },
      { tier: 'standard', name: 'Standard Protection', price: 7999, popular: true, features: ['Includes Basic Package', 'MSME Concession Certificate Assistance', 'Examination Report Review', '1-Year Status Tracking'] },
      { tier: 'premium', name: 'Comprehensive TM Guard', price: 9999, features: ['Includes Standard Package', 'Reply to Formal TM Examination Objection', 'Logo + Brand Name Dual Filing', 'Dedicated IP Attorney Assistance'] }
    ],
    questionnaire: [
      {
        id: 1,
        question: "What brand element do you want to protect?",
        description: "Trademarks cover wordmarks, stylized logos, or combination brand marks.",
        options: [
          { label: "Brand Name Text Only (Wordmark)", value: "wordmark", subtext: "Provides broadest textual protection" },
          { label: "Brand Logo / Graphic Mark", value: "logo", subtext: "Protects visual icon & design" },
          { label: "Both Brand Name + Logo", value: "both_name_logo", subtext: "Comprehensive brand defense" }
        ]
      },
      {
        id: 2,
        question: "Is your brand already being used in commerce or is it a proposed brand?",
        description: "User date proof (invoices/website domain) grants priority rights under Indian TM Law.",
        options: [
          { label: "Already in use (Prior User Date)", value: "prior_use", subtext: "Requires User Affidavit & invoice proof" },
          { label: "Proposed to be used (New Brand)", value: "proposed_use", subtext: "Fresh application filing" }
        ]
      },
      {
        id: 3,
        question: "Do you have an MSME / Udyam Certificate for your business?",
        description: "Government TM filing fee is ₹4,500 for MSME/Individuals vs ₹9,000 for non-MSME companies.",
        options: [
          { label: "Yes, have MSME Certificate", value: "has_msme", subtext: "Qualifies for ₹4,500 govt fee discount" },
          { label: "No, but want Fingard to apply MSME", value: "need_msme", subtext: "We register your MSME first" },
          { label: "Individual Applicant", value: "individual", subtext: "Individual fee rate applies" }
        ]
      },
      {
        id: 4,
        question: "Which main business sector does your brand operate in?",
        description: "Helps us select the exact Trademark Class (Classes 1-45).",
        options: [
          { label: "Software / Tech / E-Commerce (Class 9 & 42)", value: "tech_class", subtext: "Digital software & services" },
          { label: "Clothing / Fashion / Apparel (Class 25)", value: "fashion_class", subtext: "Garments & footwear" },
          { label: "Food & Beverage / Restaurant (Class 29, 30 & 43)", value: "food_class", subtext: "Food items & dining" },
          { label: "Consulting / Financial / Healthcare", value: "services_class", subtext: "Professional services" }
        ]
      }
    ],
    faqs: [
      { question: "How long does it take to get the ® registered symbol?", answer: "While you can use ™ immediately after filing, final ® registration takes 6 to 12 months subject to Registry examination." },
      { question: "What happens if someone files an objection against my mark?", answer: "Our Senior IP Advocates draft formal legal responses to Registry examination objections." }
    ]
  },

  'gst-registration': {
    id: 'gst-registration',
    title: 'GST Registration (New GSTIN Allotment)',
    category: 'gst',
    categoryTitle: 'GST Services',
    tagline: 'Get your 15-digit GSTIN assigned in 3-5 days with 100% online document processing & verification.',
    startingPrice: 1999,
    timeline: '3-5 Business Days',
    overview: 'GST Registration is mandatory for businesses with turnover exceeding ₹20/40 Lakhs, e-commerce sellers, service exporters, and interstate traders. Obtain your official 15-digit GSTIN with complete CA verification.',
    documentsRequired: [
      'PAN Card of Proprietor / Partners / Directors',
      'Aadhaar Card (linked with Mobile Number for OTP)',
      'Passport size photograph',
      'Registered Business Place Proof (Electricity Bill/Property Tax)',
      'NOC from Property Owner & Rent Agreement',
      'Cancelled Cheque / Bank Statement'
    ],
    processSteps: [
      { step: 1, title: 'Document Verification & TRN Allotment', desc: 'Verify Aadhaar eKYC & generate Temporary Reference Number (TRN).' },
      { step: 2, title: 'REG-01 Form Submission', desc: 'Fill detailed business parameters, principal place of business & HSN codes.' },
      { step: 3, title: 'Aadhaar Biometric eKYC Verification', desc: 'Complete instant OTP authentication via Aadhaar mobile link.' },
      { step: 4, title: 'GST Certificate Issuance (REG-06)', desc: 'Receive official GSTIN certificate with login credentials.' }
    ],
    pricingPlans: [
      { tier: 'basic', name: 'Basic GST Registration', price: 1999, features: ['GST REG-01 Submission', 'Aadhaar Authentication Support', 'REG-06 GST Certificate', 'Standard Support'] },
      { tier: 'standard', name: 'Standard GST + LUT Bundle', price: 2999, popular: true, features: ['Includes Basic Package', 'Letter of Undertaking (LUT) for Exporters', 'Clarification (REG-03) Response Handling', '1st Month Return Filing Assistance'] },
      { tier: 'premium', name: 'Complete GST Setup', price: 4999, features: ['Includes Standard Package', '3-Month GSTR-1 & GSTR-3B Return Filing', 'E-Way Bill Account Setup', 'Dedicated CA Advisory'] }
    ],
    questionnaire: [
      {
        id: 1,
        question: "What is your primary reason for obtaining GST Registration?",
        description: "Different registration triggers determine whether voluntary or mandatory GST rules apply.",
        options: [
          { label: "Crossed Turnover Limit (>₹20L / ₹40L)", value: "turnover_limit", subtext: "Mandatory statutory compliance" },
          { label: "Selling on E-Commerce (Amazon/Flipkart/Shopify)", value: "e_commerce", subtext: "Mandatory for online commerce" },
          { label: "Voluntary Registration for B2B Invoicing", value: "voluntary", subtext: "Pass Input Tax Credit (ITC) to clients" },
          { label: "Software / Service Export outside India", value: "export_service", subtext: "Requires zero-rated GST & LUT" }
        ]
      },
      {
        id: 2,
        question: "What type of business entity is registering for GST?",
        description: "Entity structure determines required signatory authorization letters.",
        options: [
          { label: "Sole Proprietorship", value: "proprietorship", subtext: "Individual PAN based" },
          { label: "Private Limited Company / OPC", value: "pvt_ltd", subtext: "Corporate PAN based" },
          { label: "Partnership Firm / LLP", value: "partnership_llp", subtext: "Partnership PAN based" }
        ]
      },
      {
        id: 3,
        question: "Is your registered premises owned, rented, or shared?",
        description: "GST officers inspect address proof strictly to prevent fraudulent registrations.",
        options: [
          { label: "Rented Commercial Office / Shop", value: "rented", subtext: "Requires Rent Agreement & Owner Electricity Bill" },
          { label: "Self-Owned Property / Home", value: "owned", subtext: "Requires Ownership Proof & Utility Bill" },
          { label: "Shared / Coworking Virtual Office", value: "virtual_office", subtext: "Requires Coworking Agreement & NOC" }
        ]
      },
      {
        id: 4,
        question: "Do you sell goods, provide services, or both?",
        description: "Determines HSN/SAC code mapping for your invoice portal.",
        options: [
          { label: "Services Only (IT, Consulting, Design)", value: "services", subtext: "SAC 6-digit codes" },
          { label: "Goods Only (Retail, Manufacturing)", value: "goods", subtext: "HSN 4/8-digit codes" },
          { label: "Both Goods & Services", value: "both_goods_services", subtext: "Combined HSN & SAC codes" }
        ]
      }
    ],
    faqs: [
      { question: "How long does GST registration take if physical verification is ordered?", answer: "If physical site verification is triggered by the GST officer, it takes 14-21 days instead of 3-5 days." },
      { question: "Can I get GST registration on my home address?", answer: "Yes, 100% legal provided you attach electricity bill and owner consent NOC." }
    ]
  },

  'gst-notice': {
    id: 'gst-notice',
    title: 'GST Legal Notice & Show Cause Notice (SCN) Response',
    category: 'gst',
    categoryTitle: 'GST Services',
    tagline: 'Expert legal response drafting by Senior GST Chartered Accountants to resolve ASMT-10, DRC-01 & SCN notices.',
    startingPrice: 2999,
    timeline: '24-48 Hours Emergency Response',
    overview: 'Received a GST Legal Notice, DRC-01, ASMT-10 mismatch notice, or Show Cause Notice (SCN)? Our specialized GST Advocates draft authoritative legal replies with ITC reconciliation to protect your business from hefty penalties and registration cancellation.',
    documentsRequired: [
      'Copy of GST Notice received (PDF)',
      'GSTR-1, GSTR-3B & GSTR-2A/2B reconciliations',
      'Relevant Sales & Purchase Invoices',
      'Bank Statements for the disputed period',
      'GSTIN Portal Login Credentials'
    ],
    processSteps: [
      { step: 1, title: 'Notice Analysis & Audit', desc: 'Deconstruct tax demand, ITC mismatch, or procedural default raised by GST officer.' },
      { step: 2, title: 'Data & Invoice Reconciliation', desc: 'Reconcile purchase register vs GSTR-2B and sales register vs GSTR-3B.' },
      { step: 3, title: 'Legal Reply Drafting', desc: 'Draft comprehensive reply backed by GST Case Laws & Judicial Precedents.' },
      { step: 4, title: 'Portal Submission & Hearing', desc: 'Submit written response on GST portal & attend personal hearing if required.' }
    ],
    pricingPlans: [
      { tier: 'basic', name: 'Notice Review & Draft', price: 2999, features: ['Notice Review & Legal Assessment', 'Written Reply Draft', 'Portal Submission', 'Standard CA Support'] },
      { tier: 'standard', name: 'ITC Reconciliation & Defense', price: 5999, popular: true, features: ['Includes Basic Package', 'Detailed GSTR-2B vs Purchase Reconciliation', 'DRC-01 / ASMT-10 Formal Defense', 'Personal Officer Follow-up'] },
      { tier: 'premium', name: 'Complete Appellate Representation', price: 9999, features: ['Includes Standard Package', 'High-Value SCN Defense (>₹5 Lakhs demand)', 'Senior GST Partner CA Representation', 'Appellate Authority Appeal Filing (Form APL-01)'] }
    ],
    questionnaire: [
      {
        id: 1,
        question: "Which form or section code is specified on the GST Notice received?",
        description: "Determines the statutory timeline and legal gravity of the response.",
        options: [
          { label: "ASMT-10 (Scrutiny of Returns / ITC Mismatch)", value: "asmt_10", subtext: "30-day response window" },
          { label: "DRC-01 / DRC-01A (Pre-Show Cause Tax Demand)", value: "drc_01", subtext: "Urgent demand resolution" },
          { label: "REG-17 / REG-31 (GSTIN Cancellation Notice)", value: "reg_17", subtext: "Immediate action to prevent suspension" },
          { label: "GSTR-3B Non-Filing / Delay Reminder", value: "non_filing", subtext: "Filing default clearance" }
        ]
      },
      {
        id: 2,
        question: "What is the primary reason for the GST notice?",
        description: "Helps our CA team pinpoint the audit focus area.",
        options: [
          { label: "Excess ITC claimed vs GSTR-2A / 2B", value: "itc_mismatch", subtext: "Purchase reconciliation required" },
          { label: "Turnover Mismatch (GSTR-1 vs GSTR-3B or E-Way Bill)", value: "tax_mismatch", subtext: "Sales reconciliation required" },
          { label: "Non-Filing of Returns for consecutive months", value: "delay_filing", subtext: "Late fee & interest calculation" },
          { label: "Physical Verification / Fake Invoice Allegation", value: "fake_invoice", subtext: "High priority legal defense" }
        ]
      },
      {
        id: 3,
        question: "What is the approximate tax demand amount claimed by the department?",
        description: "Determines whether partner-level GST litigation is required.",
        options: [
          { label: "Below ₹1 Lakh", value: "below_1l", subtext: "Standard reconciliation response" },
          { label: "₹1 Lakh to ₹10 Lakhs", value: "1l_10l", subtext: "Detailed legal submission required" },
          { label: "Above ₹10 Lakhs", value: "above_10l", subtext: "Senior Advocate representation required" }
        ]
      },
      {
        id: 4,
        question: "How many days remain before the reply deadline mentioned in the notice?",
        description: "Statutory deadlines must be met to avoid ex-parte penalty orders.",
        options: [
          { label: "Urgent: Less than 7 Days remaining", value: "urgent", subtext: "Immediate fast-track processing" },
          { label: "7 to 15 Days remaining", value: "moderate", subtext: "Standard response timeline" },
          { label: "More than 15 Days remaining", value: "adequate", subtext: "Comprehensive audit timeframe" }
        ]
      }
    ],
    faqs: [
      { question: "What happens if I ignore a GST Show Cause Notice?", answer: "Failure to reply leads to ex-parte assessment orders under Section 73/74 with 100% penalty and bank account attachment." },
      { question: "Can suspended GSTIN be restored by replying to notice?", answer: "Yes, replying with PRO-06/REG-18 with proper compliance proof can revoke suspension." }
    ]
  },

  'company-annual-compliance': {
    id: 'company-annual-compliance',
    title: 'Pvt Ltd Company Annual MCA Compliance',
    category: 'mca',
    categoryTitle: 'MCA & Compliances',
    tagline: 'Complete annual statutory ROC filing (AOC-4, MGT-7, ADT-1, DPT-3 & DIR-3 KYC) with zero late fees.',
    startingPrice: 8999,
    timeline: '7-10 Business Days',
    overview: 'Every Private Limited Company incorporated in India must complete annual MCA compliance regardless of turnover or profit. We manage your complete secretarial audit, financial statement filing (AOC-4), annual return (MGT-7), director KYC, and board resolutions.',
    documentsRequired: [
      'Audited Balance Sheet & Profit & Loss Account',
      'Auditor Report & Notes to Accounts',
      'Director Identification Numbers (DIN) & Class-3 DSCs',
      'List of Shareholders & Share Transfer records',
      'Bank Statements for the Financial Year'
    ],
    processSteps: [
      { step: 1, title: 'Financial Audit & Accounts Finalization', desc: 'Review audited financials and statutory auditor notes.' },
      { step: 2, title: 'Board Resolutions & Minutes Preparation', desc: 'Draft AGM notice, Board Report & Statutory Minutes.' },
      { step: 3, title: 'Form AOC-4 E-Filing', desc: 'File Financial Statements with MCA Registrar of Companies (ROC).' },
      { step: 4, title: 'Form MGT-7 / MGT-7A E-Filing', desc: 'File Annual Return with shareholding pattern within 60 days of AGM.' }
    ],
    pricingPlans: [
      { tier: 'basic', name: 'Essential ROC Compliance', price: 8999, features: ['AOC-4 Financial Statement Filing', 'MGT-7 / 7A Annual Return Filing', 'DIR-3 Director eKYC (2 Directors)', 'Filing SRN Receipts'] },
      { tier: 'standard', name: 'Standard Corporate Package', price: 12999, popular: true, features: ['Includes Basic Package', 'ADT-1 Auditor Appointment Filing', 'DPT-3 Return of Deposits/Loans Filing', 'Board Resolutions & Minutes Drafting', 'Dedicated CS Support'] },
      { tier: 'premium', name: 'Complete CS Retainer', price: 18999, features: ['Includes Standard Package', 'Statutory Register Maintenance', 'Income Tax Return (ITR-6) Filing', 'BEN-2 Significant Beneficial Owner Filing', '1-Year Company Secretary Retainer'] }
    ],
    questionnaire: [
      {
        id: 1,
        question: "Is your company active, dormant, or newly incorporated?",
        description: "First-year companies have specific 180-day Commencement of Business (INC-20A) rules.",
        options: [
          { label: "1st Year Company (Newly Incorporated)", value: "first_year", subtext: "Requires INC-20A + First Auditor ADT-1" },
          { label: "Active Operating Company", value: "active", subtext: "Regular AOC-4 & MGT-7 annual filings" },
          { label: "Dormant / No Transactions", value: "dormant", subtext: "Nil filing maintenance" }
        ]
      },
      {
        id: 2,
        question: "Have you finalized and audited your Balance Sheet with a Chartered Accountant?",
        description: "ROC filings require signed Auditor's Report & Financial Statements.",
        options: [
          { label: "Yes, Audited Financials Ready", value: "audit_ready", subtext: "Ready for AOC-4 e-filing" },
          { label: "No, need Fingard CA team for Audit", value: "need_audit", subtext: "Includes Accounting & CA Audit service" }
        ]
      },
      {
        id: 3,
        question: "Are all Director DINs active with DIR-3 KYC completed for this year?",
        description: "Deactivated DINs incur a mandatory ₹5,000 late fee per director.",
        options: [
          { label: "Yes, DIR-3 KYC completed", value: "kyc_active", subtext: "No penalty charges" },
          { label: "No / Deactivated DIN (Needs KYC)", value: "kyc_deactive", subtext: "Includes DIR-3 KYC update" }
        ]
      },
      {
        id: 4,
        question: "Are any ROC filings from previous financial years overdue?",
        description: "MCA charges ₹100 per day per form for delayed filings without any upper cap.",
        options: [
          { label: "Up to date (No past delay)", value: "on_time", subtext: "Standard compliance pricing" },
          { label: "Past filings overdue (Late penalty running)", value: "overdue", subtext: "Fast-track regularization required" }
        ]
      }
    ],
    faqs: [
      { question: "What is the penalty for late AOC-4 or MGT-7 filing?", answer: "MCA charges a mandatory penalty of ₹100 per day for each delayed filing, plus additional fines on directors." },
      { question: "Is INC-20A mandatory for new companies?", answer: "Yes, INC-20A must be filed within 180 days of incorporation before starting business operations or issuing shares." }
    ]
  }
};

export const getServiceDetailData = (serviceId: string): ServiceDetailData => {
  if (masterServicesData[serviceId]) {
    return masterServicesData[serviceId];
  }

  // Fallback template for any other service ID
  const formattedTitle = serviceId
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

  return {
    id: serviceId,
    title: formattedTitle,
    category: 'startups',
    categoryTitle: 'Financial & Legal Compliance',
    tagline: `Professional ${formattedTitle} services by Senior Chartered Accountants & Tax Experts.`,
    startingPrice: 2999,
    timeline: '3-5 Business Days',
    overview: `Our ${formattedTitle} service provides complete end-to-end statutory compliance, official document preparation, government portal submissions, and dedicated expert guidance to keep your business 100% compliant.`,
    documentsRequired: [
      'PAN Card & Aadhaar Card of Applicant',
      'Proof of Business Entity (COI / Partnership Deed / GST)',
      'Bank Statement / Cancelled Cheque',
      'Address Proof (Utility Bill / Rent Agreement)',
      'Relevant Authorisation Letter / Power of Attorney'
    ],
    processSteps: [
      { step: 1, title: 'Document Review & Verification', desc: 'Our CA team conducts thorough validation of your documents.' },
      { step: 2, title: 'Application Preparation', desc: 'Drafting legal submissions & verifying government portal parameters.' },
      { step: 3, title: 'Official Portal Filing', desc: 'Submitting statutory forms to relevant government authorities.' },
      { step: 4, title: 'Certificate Delivery & Support', desc: 'Delivering official government certificates & compliance records.' }
    ],
    pricingPlans: [
      { tier: 'basic', name: 'Basic Filing Plan', price: 2999, features: ['Document Verification', 'Standard Form Submission', 'Government Receipt', 'Email Support'] },
      { tier: 'standard', name: 'Standard Professional Plan', price: 4999, popular: true, features: ['Includes Basic Package', 'Priority Processing', 'Notice / Clarification Defense', 'Dedicated CA Support'] },
      { tier: 'premium', name: 'Complete Annual Advisory', price: 7999, features: ['Includes Standard Package', '1-Year Retainer Consultation', 'Allied Compliance Filing', 'Senior Partner Review'] }
    ],
    questionnaire: [
      {
        id: 1,
        question: `What is your primary requirement for ${formattedTitle}?`,
        description: "Select the specific service scope your business currently requires.",
        options: [
          { label: "New Application / Registration", value: "new_app", subtext: "Fresh filing with authorities" },
          { label: "Annual Renewal / Compliance", value: "renewal", subtext: "Periodic statutory maintenance" },
          { label: "Notice Response / Modification", value: "notice_mod", subtext: "Update or legal clarification" }
        ]
      },
      {
        id: 2,
        question: "What is your registered business structure?",
        description: "Structure dictates statutory compliance requirements.",
        options: [
          { label: "Sole Proprietorship / Individual", value: "proprietor", subtext: "Individual applicant" },
          { label: "Private Limited / OPC", value: "pvt_ltd", subtext: "Corporate entity" },
          { label: "LLP / Partnership Firm", value: "llp_firm", subtext: "Partnership entity" }
        ]
      },
      {
        id: 3,
        question: "What is your estimated Annual Turnover?",
        description: "Helps us assess statutory threshold applicability.",
        options: [
          { label: "Below ₹20 Lakhs", value: "below_20l", subtext: "Small scale business" },
          { label: "₹20 Lakhs to ₹1 Crore", value: "20l_1cr", subtext: "Growing business" },
          { label: "Above ₹1 Crore", value: "above_1cr", subtext: "Enterprise scale" }
        ]
      },
      {
        id: 4,
        question: "How quickly do you need this filing completed?",
        description: "Select your desired turnaround priority.",
        options: [
          { label: "Standard (3-5 Business Days)", value: "standard_priority", subtext: "Normal queue priority" },
          { label: "Urgent Fast-Track (24-48 Hours)", value: "fast_track", subtext: "Top priority CA assignment" }
        ]
      }
    ],
    faqs: [
      { question: `Why is ${formattedTitle} important for my business?`, answer: "It ensures full legal compliance, avoids heavy government penalties, and maintains business credibility." },
      { question: "Will I get dedicated CA assistance throughout the process?", answer: "Yes, a senior CA/CS will handle your entire filing from start to finish." }
    ]
  };
};
